import React from 'react';
import {
  Phone,
  Mail,
  FileText,
  AlertTriangle,
  Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import type { FormSubmission } from '../../../types';

interface ClientInfoTabProps {
  client: FormSubmission;
  onCall: (phone: string) => void;
  onEmail: (email: string) => void;
}

export default function ClientInfoTab({ client, onCall, onEmail }: ClientInfoTabProps) {
  // Télécharger le PDF d’estimation client (stocké dans Supabase)
  const handleDownloadPDF = async () => {
    try {
      // Appel à votre endpoint qui renvoie { url: 'https://…supabase/storage/…' }
      const response = await fetch(`/api/download-estimation-pdf?id=${client.id}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur lors du téléchargement');
      // Ouvre directement l’URL renvoyée
      window.open(data.url, '_blank');
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  // Télécharger le PDF d’estimation complète (généré par /api/download-complete-estimation)
  const handleDownloadCompleteEstimation = async () => {
    try {
      const response = await fetch(`/api/download-complete-estimation?id=${client.id}`);
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Erreur lors du téléchargement de l'estimation complète");
      }
      // Le serveur renvoie maintenant un buffer PDF (content-type: application/pdf)
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `estimation_complete_${client.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading complete estimation:', error);
      toast.error("Erreur lors du téléchargement de l'estimation complète");
    }
  };

  // Parser les JSON stockés en base, ou renvoyer un tableau vide
  const biensExclus: any[] = Array.isArray(client.biens_exclus)
    ? client.biens_exclus
    : (() => {
        try {
          return JSON.parse(client.biens_exclus || '[]');
        } catch {
          return [];
        }
      })();

  // Transformation des données brutes pour le graphique d'évolution des prix
  // On part de client.evolution_prix (JSON listant les mutations), et on crée un tableau { annee, prix_m2 }
  const evolutionPrix: { annee: number; prix_m2: number }[] = (() => {
    let raw: any[] = [];
    if (Array.isArray(client.evolution_prix)) {
      raw = client.evolution_prix;
    } else {
      try {
        raw = JSON.parse(client.evolution_prix || '[]');
      } catch {
        raw = [];
      }
    }
    // Pour chaque enregistrement, extraire l'année et calculer le prix/m²
    const mapped = raw.map((item: any) => {
      // date_mutation au format "YYYY-MM-DD"
      const year = parseInt(String(item.date_mutation).substring(0, 4), 10) || NaN;
      const prixM2 =
        item.surface_reelle_bati && item.surface_reelle_bati > 0
          ? item.valeur_fonciere / item.surface_reelle_bati
          : 0;
      return { annee: year, prix_m2: prixM2 };
    });
    // Trier par année croissante
    mapped.sort((a, b) => a.annee - b.annee);
    return mapped;
  })();

  // Parser les logs système stockés en base, ou renvoyer un tableau vide
  const sysLogs: string[] = Array.isArray(client.sys_logs)
    ? client.sys_logs
    : (() => {
        try {
          return JSON.parse(client.sys_logs || '[]');
        } catch {
          return [];
        }
      })();

  return (
    <div className="space-y-6 text-[0.91rem] max-h-[calc(100vh-300px)] overflow-y-auto p-4 bg-white rounded-lg shadow-md">

      {/* -------------------- INFOS PERSONNELLES & CONTACT -------------------- */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">Informations personnelles</h3>
          <div className="space-y-2">
            <p>
              <span className="font-medium">Nom:</span> {client.prenom} {client.nom}
            </p>
            <p>
              <span className="font-medium">Email:</span> {client.email}
            </p>
            <p>
              <span className="font-medium">Téléphone:</span> {client.telephone}
            </p>
            <div className="flex space-x-2 mt-2">
              <button
                onClick={() => onCall(client.telephone)}
                className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <Phone className="h-4 w-4 mr-2" /> Appeler
              </button>
              <button
                onClick={() => onEmail(client.email)}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Mail className="h-4 w-4 mr-2" /> Email
              </button>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                <FileText className="h-4 w-4 mr-2" /> Télécharger PDF
              </button>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Détails du bien</h3>
          <div className="space-y-2">
            <p>
              <span className="font-medium">Type:</span> {client.type_propriete}
            </p>
            <p>
              <span className="font-medium">Surface:</span> {client.surface} m²
            </p>
            <p>
              <span className="font-medium">Pièces:</span> {client.nb_pieces}
            </p>
            <p>
              <span className="font-medium">Adresse:</span> {client.adresse}
            </p>
            <p>
              <span className="font-medium">État:</span> {client.etat_immeuble}
            </p>
          </div>
        </div>
      </div>

      {/* -------------------- ESTIMATION COMPLÈTE -------------------- */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Estimation complète</h3>
          <button
            onClick={handleDownloadCompleteEstimation}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Télécharger l'estimation complète
          </button>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md max-h-[520px] overflow-y-auto space-y-4">
          <p>
            <strong>Prix au m² brut :</strong> {client.prix_m2_brut} €
          </p>
          <p>
            <strong>Prix au m² corrigé :</strong> {client.prix_m2_corrige} €
          </p>
          <p>
            <strong>Estimation totale :</strong> {client.estimation_totale} €
          </p>
          <p>
            <strong>Niveau de fiabilité :</strong> {client.niveau_fiabilite}
          </p>

          <div>
            <h4 className="font-semibold mt-4 mb-2">Caractéristiques détaillées :</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Chambres : {client.nb_chambres}</li>
              <li>Salles de bain : {client.nbr_salle_de_bain}</li>
              <li>
                Étage du bien : {client.etage_bien} / {client.nbre_etages}
              </li>
              <li>Année de construction : {client.annee_construction}</li>
              <li>Ascenseur : {client.ascenseur ? 'Oui' : 'Non'}</li>
              <li>Toilettes séparés : {client.toilettes_separe ? 'Oui' : 'Non'}</li>
              <li>Type de cuisine : {client.type_cuisine}</li>
              <li>
                Type de chauffage : {client.type_chauffage} ({client.nature_chauffage})
              </li>
              <li>Exposition : {client.exposition}</li>
              <li>Sécurité : {client.securite?.join(', ')}</li>
              <li>Vitrage : {client.type_vitrage}</li>
              <li>Type de parking : {client.type_parking}</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mt-4 mb-2">État du bien :</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Immeuble : {client.etat_immeuble}</li>
              <li>Façade : {client.etat_facade}</li>
              <li>Parties communes : {client.etat_partie_communes}</li>
              <li>Cuisine : {client.etat_cuisine}</li>
              <li>Salle de bain : {client.etat_salle_de_bain}</li>
              <li>Sols : {client.etat_sols}</li>
              <li>Fenêtres : {client.etat_fenetres}</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mt-4 mb-2">Annexes :</h4>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Balcon / Terrasse : {client.balcon_terasse ?? 'Non'}</li>
              <li>Jardin : {client.jardin ?? 'Non'}</li>
              <li>Cave : {client.cave ? 'Oui' : 'Non'}</li>
              <li>Garage : {client.garage ? 'Oui' : 'Non'}</li>
              <li>Place de parking : {client.place_parking ? 'Oui' : 'Non'}</li>
            </ul>
          </div>

          {/* -------------------- VENTES COMPARABLES -------------------- */}
          {client.ventes_comparables &&
            Array.isArray(client.ventes_comparables) &&
            client.ventes_comparables.length > 0 && (
              <div>
                <h4 className="font-semibold mt-4 mb-2">Ventes comparables :</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border px-2 py-1">Date</th>
                        <th className="border px-2 py-1">Adresse</th>
                        <th className="border px-2 py-1">Surface</th>
                        <th className="border px-2 py-1">Pièces</th>
                        <th className="border px-2 py-1">Prix (€)</th>
                        <th className="border px-2 py-1">Prix/m²</th>
                      </tr>
                    </thead>
                    <tbody>
                      {client.ventes_comparables.map((vente: any, idx: number) => (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-1">{vente.date_mutation}</td>
                          <td className="px-2 py-1">{vente.adresse_complete}</td>
                          <td className="px-2 py-1">{vente.surface_reelle_bati} m²</td>
                          <td className="px-2 py-1">{vente.nombre_pieces_principales}</td>
                          <td className="px-2 py-1">{vente.valeur_fonciere.toLocaleString()} €</td>
                          <td className="px-2 py-1">
                            {(vente.valeur_fonciere / vente.surface_reelle_bati).toFixed(0)} €/m²
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* -------------------- ÉVOLUTION DES PRIX (GRAPHIQUE) -------------------- */}
          {evolutionPrix.length > 0 && (
            <div>
              <h4 className="font-semibold mt-6 mb-2">📈 Évolution des prix</h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={evolutionPrix}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="annee" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => `${Math.round(value)} €/m²`} />
                  <Line type="monotone" dataKey="prix_m2" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* -------------------- BIENS EXCLUS -------------------- */}
          {biensExclus.length > 0 && (
            <div>
              <h4 className="font-semibold mt-6 mb-2">🏷️ Biens exclus du calcul</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 text-sm">
                  <thead className="bg-yellow-100 text-yellow-900">
                    <tr>
                      <th className="border px-2 py-1">Date</th>
                      <th className="border px-2 py-1">Adresse</th>
                      <th className="border px-2 py-1">Surface</th>
                      <th className="border px-2 py-1">Pièces</th>
                      <th className="border px-2 py-1">Prix (€)</th>
                      <th className="border px-2 py-1">Prix/m²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {biensExclus.map((bien: any, idx: number) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">{bien.date_mutation}</td>
                        <td className="px-2 py-1">{bien.adresse_complete}</td>
                        <td className="px-2 py-1">{bien.surface} m²</td>
                        <td className="px-2 py-1">{bien.nb_pieces}</td>
                        <td className="px-2 py-1">
                          {bien.valeur_fonciere?.toLocaleString()} €
                        </td>
                        <td className="px-2 py-1">{bien.prix_m2?.toFixed(0)} €/m²</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* -------------------- LOGS SYSTÈME -------------------- */}
          {sysLogs.length > 0 && (
            <div className="bg-gray-100 p-3 rounded-lg mt-6">
              <h4 className="text-sm font-semibold mb-2 flex items-center text-gray-700">
                <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" /> Données DVF brutes (logs système)
              </h4>
              <div className="text-xs text-gray-800 whitespace-pre-wrap leading-snug space-y-1">
                {sysLogs.map((log: string, i: number) => (
                  <div key={i} className="border-l-2 border-gray-300 pl-2 ml-1">
                    {log}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}